"use client"

import type { ComponentType, SVGProps } from "react"
import { useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconCopyOutline18 as CopyIcon,
	IconArrowUpRightOutline18 as ExternalIcon,
} from "nucleo-ui-outline-18"
import { useCopyToClipboard, useDebounce } from "react-use"

import type { McpClientId } from "./mcp-clients"
import type { OptionCard } from "./option-cards"
import { ClaudeIcon } from "../icons/project/claude-icon"
import { CursorIcon } from "../icons/project/cursor-icon"
import { OpenAIIcon } from "../icons/project/openai-icon"
import { VSCodeIcon } from "../icons/project/vscode-icon"
import { Button } from "./button"
import { CopyButton } from "./copy-button"
import { Divider } from "./divider"
import {
	findMcpClient,
	MCP_CLIENTS,
	MCP_SERVER_URL,
	MCP_START_PROMPT,
	MCP_USE_CASES,
} from "./mcp-clients"
import { OptionCards } from "./option-cards"

export { MCP_DOCS_ARTICLE_ID } from "./mcp-clients"

export const CONNECT_MCP_TITLE = "Connect MCP"
export const CONNECT_MCP_DESCRIPTION =
	"Add Productlane to Claude Code, Cursor, ChatGPT, or any MCP client. Authorize in the browser when it opens. No API key needed."

type Icon = ComponentType<SVGProps<SVGSVGElement>>

const CLIENT_ICONS: Record<McpClientId, Icon> = {
	"claude-code": ClaudeIcon as Icon,
	cursor: CursorIcon as Icon,
	vscode: VSCodeIcon as Icon,
	claude: ClaudeIcon as Icon,
	chatgpt: OpenAIIcon as Icon,
}

const CLIENT_OPTIONS: OptionCard<McpClientId>[] = MCP_CLIENTS.map((client) => ({
	value: client.id,
	label: client.label,
	Icon: CLIENT_ICONS[client.id],
}))

function CommandBlock({ command }: { command: string }) {
	return (
		<div className="flex items-start gap-1 rounded-lg bg-gray-100 py-1 pl-3 pr-1 ring-1 ring-black/[0.07] dark:bg-gray-850 dark:ring-white/[0.07]">
			<code className="min-w-0 flex-1 whitespace-pre-wrap break-words py-1.5 font-mono text-[0.78rem] leading-5 text-secondary">
				{command}
			</code>
			<CopyButton value={command} tooltip="Copy command" className="mt-0.5" />
		</div>
	)
}

function Steps({ steps }: { steps: string[] }) {
	return (
		<ol className="space-y-1.5">
			{steps.map((step, index) => (
				<li key={step} className="flex items-start gap-2.5 text-sm">
					<span className="mt-[1px] flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-gray-100 text-[0.7rem] font-medium text-tertiary dark:bg-gray-800">
						{index + 1}
					</span>
					<span className="text-secondary">{step}</span>
				</li>
			))}
		</ol>
	)
}

function useCopied(value: string) {
	const [, copy] = useCopyToClipboard()
	const [isCopied, setIsCopied] = useState(false)
	useDebounce(
		() => {
			setIsCopied(false)
		},
		1200,
		[isCopied],
	)
	return {
		isCopied,
		copy: () => {
			copy(value)
			setIsCopied(true)
		},
	}
}

// Every client takes the same URL, so it sits above the client picker.
function ServerUrlButton() {
	const { isCopied, copy } = useCopied(MCP_SERVER_URL)
	return (
		<Button
			variant="outline"
			size="lg"
			aria-label="Copy server URL"
			iconRight={isCopied ? Check : CopyIcon}
			// The shared button caps at max-w-xs; this one spans the content.
			className="h-11 w-full max-w-none justify-start px-3.5"
			wrapperClassName="min-w-0 flex-1 justify-between gap-3"
			iconRightClassName="h-3.5 w-3.5 transform-gpu"
			onClick={copy}
		>
			<span className="flex min-w-0 items-center gap-3">
				<span className="flex-none text-xs font-medium text-tertiary">
					Server URL
				</span>
				<code className="min-w-0 truncate font-mono text-[0.8rem] text-primary">
					{MCP_SERVER_URL}
				</code>
			</span>
		</Button>
	)
}

function CopyPromptButton() {
	const { isCopied, copy } = useCopied(MCP_START_PROMPT)
	return (
		<Button
			variant="outline"
			Icon={isCopied ? Check : CopyIcon}
			className="flex-none"
			onClick={copy}
		>
			Copy prompt
		</Button>
	)
}

export function ConnectMcpContent({
	variant = "dialog",
}: {
	variant?: "dialog" | "page"
}) {
	const isPage = variant === "page"
	const [clientId, setClientId] = useState<McpClientId>("claude-code")
	const client = findMcpClient(clientId)

	return (
		<div className={isPage ? "space-y-6" : "space-y-5"}>
			<ServerUrlButton />

			<OptionCards
				name="mcp-client"
				value={clientId}
				options={CLIENT_OPTIONS}
				onValueChange={setClientId}
			/>

			{/* Reserved for the tallest client (VS Code, 112px) so switching clients cannot resize the card. */}
			<div className={isPage ? "space-y-3" : "min-h-[7rem] space-y-3"}>
				<p className="text-sm text-secondary">{client.hint}</p>
				{client.command ? <CommandBlock command={client.command} /> : null}
				{client.deeplink ? (
					<Button
						variant="outline"
						size="lg"
						iconRight={ExternalIcon}
						onClick={() => {
							window.location.href = client.deeplink!
						}}
					>
						{client.deeplinkLabel}
					</Button>
				) : null}
				{client.steps ? <Steps steps={client.steps} /> : null}
			</div>

			<Divider />

			<div className="flex items-center justify-between gap-4">
				<div className="min-w-0">
					<div className="text-sm font-medium text-primary">
						Set up your workspace
					</div>
					<p className="mt-0.5 text-sm text-secondary">
						Paste this prompt into the client once it restarts.
					</p>
				</div>
				<CopyPromptButton />
			</div>

			{isPage ? (
				<>
					<Divider />
					<div>
						<div className="text-sm font-medium text-primary">
							What you can do once connected
						</div>
						<ul className="mt-2.5 space-y-2">
							{MCP_USE_CASES.map((useCase) => (
								<li
									key={useCase}
									className="flex items-start gap-2.5 text-sm text-secondary"
								>
									<span className="mt-[7px] h-1.5 w-1.5 flex-none translate-x-[2px] rounded-full bg-green-500" />
									<span>{useCase}</span>
								</li>
							))}
						</ul>
					</div>
				</>
			) : null}
		</div>
	)
}
